"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useState } from "react";

import type { KundaliFormCardProps } from "./KundaliFormCard";

const DEFAULT_DEFER_MS = 3600;
const FORM_EVENTS = ["pointerdown", "keydown", "touchstart", "focusin"] as const;

const KundaliFormCard = dynamic(() => import("./KundaliFormCard"), {
  ssr: false,
  loading: () => <KundaliFormLoadingReserve />,
});

type DeferredKundaliFormCardProps = KundaliFormCardProps & {
  deferMs?: number;
  fallbackTitle?: string;
  fallbackBody?: string;
  fallbackSubmitLabel?: string;
};

export default function DeferredKundaliFormCard({
  deferMs = DEFAULT_DEFER_MS,
  fallbackTitle,
  fallbackBody,
  fallbackSubmitLabel,
  ...formProps
}: DeferredKundaliFormCardProps) {
  const [ready, setReady] = useState(false);
  const activate = useCallback(() => setReady(true), []);

  useEffect(() => {
    if (ready) return;

    const timer = window.setTimeout(activate, deferMs);
    const options: AddEventListenerOptions = { once: true, passive: true };
    for (const eventName of FORM_EVENTS) {
      window.addEventListener(eventName, activate, options);
    }

    return () => {
      window.clearTimeout(timer);
      for (const eventName of FORM_EVENTS) {
        window.removeEventListener(eventName, activate);
      }
    };
  }, [activate, deferMs, ready]);

  if (ready) return <KundaliFormCard {...formProps} />;

  return (
    <KundaliFormShell
      containerClassName={formProps.containerClassName}
      title={fallbackTitle}
      body={fallbackBody}
      submitLabel={fallbackSubmitLabel ?? formProps.submitLabel}
      onActivate={activate}
    />
  );
}

function KundaliFormShell({
  containerClassName = "mx-auto max-w-md px-4 sm:px-6 pb-16",
  title = "Generate your free janam kundali",
  body = "Share your birth details to unlock chart-grounded dosha, dasha, and upay guidance.",
  submitLabel = "Continue",
  onActivate,
}: {
  containerClassName?: string;
  title?: string;
  body?: string;
  submitLabel?: string;
  onActivate: () => void;
}) {
  return (
    <div className={containerClassName} onPointerDown={onActivate} onFocus={onActivate}>
      <form
        aria-busy="true"
        className="card-sacred rounded-2xl p-5 sm:p-6"
        onSubmit={(event) => {
          event.preventDefault();
          onActivate();
        }}
      >
        <p
          className="mb-1 text-[12px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: "var(--ish-accent)" }}
        >
          Free Vedic chart
        </p>
        <h2 className="text-[22px] font-bold leading-tight" style={{ color: "var(--ish-dark)" }}>
          {title}
        </h2>
        <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--ish-text-muted)" }}>
          {body}
        </p>
        <div className="mt-5 grid gap-3">
          <ShellField label="Name" />
          <ShellField label="Birth date" />
          <ShellField label="Birth time" />
          <ShellField label="Birth place" />
        </div>
        <button
          type="submit"
          className="btn-sacred mt-5 w-full rounded-lg py-3.5 text-[14px] font-semibold"
        >
          {submitLabel}
        </button>
        <p className="mt-3 text-center text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
          No signup required.
        </p>
      </form>
    </div>
  );
}

function KundaliFormLoadingReserve() {
  return (
    <div className="mx-auto max-w-md px-4 sm:px-6 pb-16" aria-hidden>
      <div className="card-sacred min-h-[420px] rounded-2xl p-5 sm:p-6" />
    </div>
  );
}

function ShellField({ label }: { label: string }) {
  return (
    <label className="block">
      <span
        className="mb-1 block text-[12px] font-medium"
        style={{ color: "var(--ish-text-muted)" }}
      >
        {label}
      </span>
      <span
        className="block h-11 rounded-lg"
        style={{
          background: "var(--ish-bg)",
          border: "1px solid var(--ish-border)",
        }}
      />
    </label>
  );
}
